import BookingGridBase from '../common/BookingGridBase';
import BookingCardKaraoke from './BookingCardKaraokev2';
import ResourceLegend from '../common/ResourceLegend';
import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import useResource from '../../../../hooks/useResource';
import { toast } from 'react-toastify';
import { createResourceColorById } from '../../../../util/resourceColorPool';

function GridKaraoke({
  bookings,
  rowCount,
  date,
  slotCapacity,
  updateBooking,
  handleOpenModal,
  entertainmentId,
  capacity,
  onBookingShift,
  entertainment,
  onDragStart,
  onDragActivated,
  onDragEnd,
  availableShiftTimes,
  draggedBookingDuration,
  isShiftable,
  isLoadingShiftTimes,
  isBookingReadyForShift,
  shiftTimeOffsetMinutes = 0,
  resourceOffsetMap = {},
}) {
  const [karaokeResources, setKaraokeResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getResourcesByTypeAndEntertainment } = useResource();

  // Fetch karaoke resources to determine capacity
  useEffect(() => {
    const fetchResources = async () => {
      if (!entertainmentId) return;

      try {
        setLoading(true);
        const resources = await getResourcesByTypeAndEntertainment('karaoke', entertainmentId);
        setKaraokeResources(resources);
      } catch (err) {
        console.error('Error fetching karaoke resources:', err);
        toast.error('Fehler beim Laden der Karaoke-Ressourcen');
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, [entertainmentId]);

  const getResourceOffset = () => 0;

  const karaokeCapacity = useMemo(() => {
    if (Array.isArray(karaokeResources) && karaokeResources.length > 0) {
      return karaokeResources.length;
    }
    return Number(slotCapacity) || 0;
  }, [karaokeResources, slotCapacity]);

  const resourceColorById = useMemo(
    () => createResourceColorById(karaokeResources),
    [karaokeResources]
  );

  const calcAttendance = useCallback(
    list => {
      const cap = karaokeCapacity;
      const times = Array.from({ length: (24 - 9) * 2 + 1 }, (_, index) => {
        const hour = 9 + Math.floor(index / 2);
        const minute = index % 2 === 0 ? '00' : '30';
        const qualifiedTime = new Date(
          date.get('year'),
          date.get('month'),
          date.get('date'),
          hour,
          minute
        );
        return {
          isFull: index % 2 === 0,
          time: `${hour}:${minute}`,
          qualifiedTime,
          i: index,
          s0: 0,
          frees0: cap,
        };
      });

      const items = Array.isArray(list) ? list : [];
      for (const bk of items) {
        const bookingType = bk?.bookingType;
        const startKey = bookingType === 'RegiondoBooking' ? 'eventStart' : 'startTime';
        const endKey = bookingType === 'RegiondoBooking' ? 'eventEnd' : 'endTime';
        const compStart = new Date(bk?.[startKey]);
        const compEnd = new Date(bk?.[endKey]);
        if (Number.isNaN(compStart.getTime()) || Number.isNaN(compEnd.getTime())) continue;

        for (const t of times) {
          if (compStart <= t.qualifiedTime && compEnd > t.qualifiedTime) {
            if (bk?.status === 'blocked' && !bk?.resourceId) {
              t.frees0 -= cap;
            } else if (bk?.status === 'blocked') {
              t.frees0 -= 1;
            } else {
              t.s0 += 1;
              t.frees0 -= 1;
            }
          }
        }
      }

      return times;
    },
    [date, karaokeCapacity]
  );

  return (
    <>
      <BookingGridBase
        bookings={bookings}
        rowCount={rowCount}
        date={date}
        slotCapacity={slotCapacity}
        calcAttendance={calcAttendance}
        capacityData={capacity}
        availableShiftTimes={availableShiftTimes}
        draggedBookingDuration={draggedBookingDuration}
        isLoadingShiftTimes={isLoadingShiftTimes}
        shiftTimeOffsetMinutes={0}
        renderLegend={() => (
          <ResourceLegend
            resources={karaokeResources}
            loading={loading}
            resourceColorById={resourceColorById}
          />
        )}
        renderBookingCard={(booking, idx, rowHeight) => (
          <BookingCardKaraoke
            key={booking._id}
            booking={booking}
            resourceColorById={resourceColorById}
            updateBookingState={updateBooking}
            handleOpenModal={handleOpenModal}
            renderAvailableSlots={false}
            rowHeight={rowHeight}
            onBookingShift={onBookingShift}
            entertainment={entertainment}
            onDragStart={onDragStart}
            onDragActivated={onDragActivated}
            onDragEnd={onDragEnd}
            availableShiftTimes={availableShiftTimes}
            isShiftable={isShiftable}
            isBookingReadyForShift={isBookingReadyForShift}
            resourceStartOffsetMinutes={getResourceOffset(booking)}
            openingGridOffset={capacity?.gridOffset || 0}
          />
        )}
        renderAvailableSlots={false}
      />
    </>
  );
}

export default memo(GridKaraoke);
